// TaskFlow · aplicación: estructura (menú lateral en escritorio, barra inferior en móvil, tres columnas),
// enrutador, tema, atajos de teclado, indicador de conexión y sincronización, instalación de la PWA.
import { api, estado, alCambiarConexion, sincronizar, contarPendientes, operacionesPendientes } from './api.js';
import { $, e, icono, aviso, deshacerUltimo, dialogo, confirmar, plural, uuid } from './util.js';
import { conectarEventos, desconectarEventos, alEvento } from './vivo.js';
import * as V from './vistas.js';
import { Nota, Versiones } from './notas.js';
import { renderDetalle, cerrarEditorDetalle, nuevaTarea } from './detalle.js';
import { pantallaEntrar, pantallaRecuperar, pedirCambioClave, Perfil, Admin, tablaAtajos } from './cuenta.js';

const RUTAS = [
  [/^\/(hoy)?$/, V.Hoy], [/^\/proximos$/, V.Proximos], [/^\/asignadas$/, V.Asignadas], [/^\/calendario$/, V.Calendario],
  [/^\/pendientes$/, V.Pendientes], [/^\/programadas$/, V.Programadas], [/^\/pilas$/, V.Pilas], [/^\/pila\/([0-9a-f-]{36})$/, V.Pila],
  [/^\/lista\/([0-9a-f-]{36})$/, V.Lista], [/^\/nota\/([0-9a-f-]{36})$/, Nota], [/^\/nota\/([0-9a-f-]{36})\/versiones$/, Versiones],
  [/^\/plantillas$/, V.Plantillas], [/^\/papelera$/, V.Papelera], [/^\/buscar$/, V.Buscar], [/^\/notificaciones$/, V.Notificaciones],
  [/^\/perfil$/, Perfil], [/^\/admin$/, Admin],
];
const NAV = [
  ['/hoy', 'Hoy', 'hoy'], ['/proximos', 'Próximos 7 días', 'semana'], ['/asignadas', 'Asignadas a mí', 'personas'], ['/calendario', 'Calendario', 'calendario'],
  ['/pendientes', 'Pendientes', 'bandeja'], ['/programadas', 'Programadas', 'reloj'], ['/pilas', 'Pilas', 'pilas'],
];
const NAV2 = [['/plantillas', 'Plantillas', 'plantilla'], ['/papelera', 'Papelera', 'papelera'], ['/notificaciones', 'Notificaciones', 'campana'], ['/perfil', 'Perfil', 'usuario']];

const raiz = document.getElementById('app');
let vistaActual = null;       // ruta de la vista del listado (columna central)
let limpiezas = [];           // funciones a ejecutar al salir de la vista
let oyentesVivo = [];
let detalleUid = null;
let instalarEvento = null;

// ---------- Tema ----------
const media = matchMedia('(prefers-color-scheme: dark)');
// Tema (sistema, claro u oscuro) y paleta (una de las 20 de VisiónFlow) los elige cada usuario en su perfil.
let paletaActual = 'leodoncel';
function aplicarTema(tema = estado.usuario?.tema || 'sistema', paleta = estado.usuario?.paleta || paletaActual) {
  const oscuro = tema === 'oscuro' || (tema === 'sistema' && media.matches);
  paletaActual = paleta || 'leodoncel';
  document.documentElement.dataset.theme = oscuro ? 'dark' : 'light';
  document.documentElement.dataset.paleta = paletaActual;
  // La barra de estado del celular toma el fondo real de la paleta elegida.
  const fondo = getComputedStyle(document.documentElement).getPropertyValue('--superficie').trim() || (oscuro ? '#111220' : '#F6F6FA');
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', fondo));
  try { localStorage.setItem('tf-tema', tema); localStorage.setItem('tf-paleta', paletaActual); } catch { /* */ }
}
media.addEventListener('change', () => aplicarTema());
try { aplicarTema(localStorage.getItem('tf-tema') || 'sistema', localStorage.getItem('tf-paleta') || 'leodoncel'); } catch { aplicarTema('sistema', 'leodoncel'); }

// ---------- Estructura ----------
function pintarMarco() {
  const u = estado.usuario;
  const navLink = ([href, txt, ic]) => `<a href="${href}" data-ruta>${icono(ic)}<span>${txt}</span><span class="contador" data-contador="${href}"></span></a>`;
  raiz.innerHTML = `<div class="marco" id="marco">
    <nav class="nav-lateral" aria-label="Navegación principal">
      <div class="marca"><img src="/iconos/cabeza.svg" alt="" width="36" height="36"><b>TaskFlow</b></div>
      <form role="search" class="buscador" id="nav-buscar"><label class="solo-lector" for="nav-q">Buscar</label>${icono('buscar')}<input id="nav-q" type="search" placeholder="Buscar" autocomplete="off"><kbd aria-hidden="true">/</kbd></form>
      <div class="rapidas"><button class="tf-boton tf-boton--principal" type="button" data-nueva="tarea" aria-keyshortcuts="N">${icono('mas')}Tarea</button><button class="tf-boton" type="button" data-nueva="nota" aria-keyshortcuts="Shift+N">${icono('nota')}Nota</button></div>
      ${NAV.map(navLink).join('')}<hr>${NAV2.map(navLink).join('')}${u.es_admin ? navLink(['/admin', 'Administración', 'escudo']) : ''}
      <button class="nav-boton" type="button" id="ver-atajos">${icono('teclado')}<span>Atajos de teclado</span></button>
      <div class="pie"><p class="meta" style="margin:0">${e(u.nombre)}</p><p class="firma" style="margin:4px 0 0;text-align:left">TaskFlow · Leo Doncel</p></div>
    </nav>
    <main class="principal" id="contenido" tabindex="-1">
      <header class="encabezado"><h1 id="titulo-pagina">TaskFlow</h1>
        <span id="sync"></span>
        <a class="boton-icono" href="/buscar" data-ruta aria-label="Buscar" id="m-buscar">${icono('buscar')}</a>
        <a class="boton-icono" href="/notificaciones" data-ruta aria-label="Notificaciones" id="campana">${icono('campana')}<span class="insignia" id="insignia" hidden></span></a></header>
      <div id="franja" class="franja-conexion"></div>
      <div id="vista"></div>
    </main>
    <aside class="detalle" id="detalle" aria-label="Detalle de la tarea"></aside>
    <nav class="barra-inferior" aria-label="Navegación principal">
      <a href="/hoy" data-ruta>${icono('hoy')}Hoy</a><a href="/proximos" data-ruta>${icono('semana')}Próximos</a><a href="/calendario" data-ruta>${icono('calendario')}Calendario</a>
      <a href="/pilas" data-ruta>${icono('pilas')}Pilas</a><button type="button" id="mas-movil" aria-haspopup="dialog">${icono('menu')}Más</button></nav>
    <button class="fab" type="button" data-nueva="tarea" aria-label="Nueva tarea">${icono('mas')}</button>
  </div>`;
  $('#nav-buscar').addEventListener('submit', (ev) => { ev.preventDefault(); ir('/buscar?q=' + encodeURIComponent($('#nav-q').value)); });
  $('#ver-atajos').addEventListener('click', verAtajos);
  $('#mas-movil').addEventListener('click', menuMovil);
  raiz.addEventListener('click', (ev) => {
    const n = ev.target.closest('[data-nueva]');
    if (n) { n.dataset.nueva === 'tarea' ? nuevaTareaAqui() : nuevaNotaAqui(); }
  });
  pintarConexion();
}

function menuMovil() {
  const d = dialogo(`<div class="cuerpo-dialogo"><h2>Más</h2><nav aria-label="Más secciones" class="tf-pila" style="gap:2px">
    ${[['/asignadas', 'Asignadas a mí', 'personas'], ['/pendientes', 'Pendientes', 'bandeja'], ['/programadas', 'Programadas', 'reloj'], ...NAV2, ...(estado.usuario.es_admin ? [['/admin', 'Administración', 'escudo']] : [])]
      .map(([h, t, i]) => `<a class="fila-enlace" href="${h}" data-ruta style="border:0;border-radius:8px">${icono(i)}<span class="texto"><b>${t}</b></span></a>`).join('')}
    <button class="fila-enlace tf-boton tf-boton--fantasma" type="button" data-nueva="nota" style="border:0;justify-content:flex-start">${icono('nota')}<span class="texto"><b>Nueva nota</b></span></button></nav>
    <div class="acciones"><button class="tf-boton" type="button" id="cerrar-mas">Cerrar</button></div></div>`);
  d.querySelector('#cerrar-mas').addEventListener('click', () => d.close());
  d.addEventListener('click', (ev) => {
    const a = ev.target.closest('a[data-ruta]'); if (a) { ev.preventDefault(); d.close(); ir(a.getAttribute('href')); }
    if (ev.target.closest('[data-nueva="nota"]')) { d.close(); nuevaNotaAqui(); }
  });
}

function verAtajos() {
  const d = dialogo(`<div class="cuerpo-dialogo"><h2>Atajos de teclado</h2>${tablaAtajos()}<div class="acciones"><button class="tf-boton tf-boton--principal" type="button" id="ok-at">Listo</button></div></div>`);
  d.querySelector('#ok-at').addEventListener('click', () => d.close());
}

// ---------- Indicadores de conexión y sincronización ----------
function pintarConexion() {
  const sync = $('#sync');
  const franja = $('#franja');
  if (!sync || !franja) return;
  franja.innerHTML = estado.enLinea ? '' : `<div class="tf-sin-conexion" role="status">${icono('sinred')}<span>Sin conexión: tus cambios se guardan en este dispositivo${estado.pendientes ? ` · ${plural(estado.pendientes, 'cambio pendiente', 'cambios pendientes')}` : ''}</span></div>`;
  if (estado.errorSync) {
    sync.innerHTML = `<button type="button" class="tf-sync tf-sync--error tf-sync-boton" id="ver-error">${icono('alerta')}No se pudieron enviar ${plural(estado.errorSync.length, 'cambio', 'cambios')}</button>`;
    $('#ver-error').addEventListener('click', () => {
      const d = dialogo(`<div class="cuerpo-dialogo"><h2>Cambios que el servidor rechazó</h2><ul>${estado.errorSync.map((x) => `<li>${e(x)}</li>`).join('')}</ul>
        <p class="meta">Suele pasar cuando alguien cambió permisos o eliminó el elemento mientras estabas sin conexión.</p>
        <div class="acciones"><button class="tf-boton" type="button" id="ok-err">Entendido</button><button class="tf-boton tf-boton--principal" type="button" id="reint">${icono('sync')}Reintentar</button></div></div>`);
      d.querySelector('#ok-err').addEventListener('click', () => { estado.errorSync = null; pintarConexion(); d.close(); });
      d.querySelector('#reint').addEventListener('click', () => { d.close(); sincronizar(); });
    });
  } else if (estado.sincronizando) sync.innerHTML = `<span class="tf-sync" role="status">${icono('sync')}Sincronizando ${plural(estado.pendientes, 'cambio', 'cambios')}…</span>`;
  else if (estado.pendientes && estado.enLinea) sync.innerHTML = `<button type="button" class="tf-sync tf-sync-boton" id="reint2">${icono('sync')}${plural(estado.pendientes, 'cambio', 'cambios')} por enviar · Reintentar</button>`;
  else if (!estado.enLinea) sync.innerHTML = `<span class="tf-sync">${icono('sinred')}Sin conexión</span>`;
  else sync.innerHTML = '';
  $('#reint2')?.addEventListener('click', () => sincronizar());
}
let antesSync = false;
alCambiarConexion(() => {
  pintarConexion();
  if (antesSync && !estado.sincronizando && !estado.pendientes && estado.enLinea && !estado.errorSync) {
    const s = $('#sync'); if (s) { s.innerHTML = `<span class="tf-sync tf-sync--ok" role="status">${icono('ok')}Sincronizado</span>`; setTimeout(() => pintarConexion(), 3000); }
  }
  antesSync = estado.sincronizando;
});
window.addEventListener('tf:sincronizado', () => recargar());

// ---------- Enrutador ----------
export function ir(ruta, { reemplazar = false } = {}) {
  if (reemplazar) history.replaceState(null, '', ruta); else history.pushState(null, '', ruta);
  enrutar();
}
function salirDeVista() { limpiezas.forEach((f) => { try { f(); } catch { /* */ } }); limpiezas = []; oyentesVivo.forEach((f) => f()); oyentesVivo = []; }

const ctx = {
  ir, recargar: () => recargar(), abrirTarea: (uid) => abrirTarea(uid), cerrarDetalle: () => cerrarDetalle(),
  refrescarLista: () => recargar(true), titulo: (t) => ponerTitulo(t), alSalir: (f) => limpiezas.push(f),
  alEventoVivo: (f) => oyentesVivo.push(alEvento(f)), aplicarTema, salir: () => salir(),
  contadorNotificaciones: (n) => pintarInsignia(n), pintarInstalar: (c) => pintarInstalar(c),
};

function ponerTitulo(t) {
  document.title = `${t} · TaskFlow`;
  const h = $('#titulo-pagina'); if (h) h.textContent = t;
}

async function enrutar() {
  const ruta = location.pathname;
  if (!estado.usuario) return;
  const mt = ruta.match(/^\/tarea\/([0-9a-f-]{36})$/);
  if (mt) {
    if (!vistaActual) await pintarVista('/hoy');
    abrirDetalle(mt[1]);
    return;
  }
  if (detalleUid) cerrarDetalle(false);
  await pintarVista(ruta);
}

let pintando = Promise.resolve();
async function pintarVista(ruta) {
  let fin; const anterior = pintando; pintando = new Promise((r) => { fin = r; });
  await anterior;
  try { await pintarVistaAhora(ruta); } finally { fin(); }
}
async function pintarVistaAhora(ruta) {
  salirDeVista();
  const [re, Vista] = RUTAS.find(([r]) => r.test(ruta)) || [null, null];
  const cont = vistaLimpia();
  vistaActual = ruta + location.search;
  marcarNav(ruta);
  if (!Vista) { cont.innerHTML = '<div class="vista"><div class="vacio"><strong>Esa página no existe.</strong><a href="/hoy" data-ruta>Ir a Hoy</a></div></div>'; return; }
  ponerTitulo(Vista.titulo);
  const params = ruta.match(re).slice(1);
  try {
    await Vista.render(cont, ctx, params);
  } catch (err) {
    cont.innerHTML = `<div class="vista"><div class="vacio"><strong>${err.sinConexion ? 'Sin conexión' : 'No se pudo cargar'}</strong>${e(err.message)}<div style="margin-top:12px"><button class="tf-boton" type="button" id="reintentar">${icono('sync')}Reintentar</button></div></div></div>`;
    $('#reintentar')?.addEventListener('click', () => recargar());
  }
  // Cambios en vivo: se recarga la vista suavemente (sin mover el foco ni el scroll).
  oyentesVivo.push(alEvento((ev) => { if (ev.tipo === 'cambio' && ev.por !== estado.usuario.id && !/^\/nota\//.test(location.pathname)) recargarSuave(); }));
  const h = $('#titulo-vista'); if (h && !detalleUid) h.focus({ preventScroll: true });
}

/** Reemplaza el contenedor de la vista por uno nuevo para que no se acumulen oyentes de eventos entre vistas. */
function vistaLimpia() {
  const viejo = $('#vista');
  const nuevo = viejo.cloneNode(false);
  nuevo.innerHTML = viejo.innerHTML;
  viejo.replaceWith(nuevo);
  return nuevo;
}

let recargaPendiente = null;
function recargarSuave() {
  clearTimeout(recargaPendiente);
  recargaPendiente = setTimeout(() => {
    // No se recarga mientras la persona escribe en un campo de la vista.
    if (document.activeElement && $('#vista')?.contains(document.activeElement) && /INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName)) return;
    recargar(true);
  }, 400);
}

async function recargar(suave = false) {
  let fin; const anterior = pintando; pintando = new Promise((r) => { fin = r; });
  await anterior;
  try { await recargarAhora(suave); } finally { fin(); }
}
async function recargarAhora(suave = false) {
  if (!estado.usuario) return;
  const y = window.scrollY;
  const foco = document.activeElement?.closest?.('[data-uid]')?.dataset.uid;
  const ruta = vistaActual ? vistaActual.split('?')[0] : location.pathname;
  if (suave && /^\/nota\//.test(ruta)) return;
  const [re, Vista] = RUTAS.find(([r]) => r.test(ruta)) || [];
  if (!Vista) return;
  salirDeVista();
  try { await Vista.render(vistaLimpia(), ctx, ruta.match(re).slice(1)); } catch (err) { if (!suave) aviso(err.message); }
  oyentesVivo.push(alEvento((ev) => { if (ev.tipo === 'cambio' && ev.por !== estado.usuario.id) recargarSuave(); }));
  window.scrollTo(0, y);
  if (foco) document.querySelector(`[data-uid="${foco}"] .tf-casilla, [data-uid="${foco}"] a`)?.focus({ preventScroll: true });
  if (detalleUid && suave) { /* el detalle tiene su propio documento en vivo */ }
}

function marcarNav(ruta) {
  document.querySelectorAll('[data-ruta]').forEach((a) => {
    const h = a.getAttribute('href');
    const activo = h === ruta || (h === '/hoy' && ruta === '/') || (h === '/pilas' && /^\/(pila|lista|nota)\//.test(ruta));
    if (activo) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
  });
}

// ---------- Detalle de tarea (tercera columna) ----------
function abrirTarea(uid) {
  history.pushState({ detalle: true }, '', '/tarea/' + uid);
  abrirDetalle(uid);
}
function abrirDetalle(uid) {
  detalleUid = uid;
  const p = $('#detalle');
  p.classList.add('abierto');
  $('#marco').classList.add('con-detalle');
  document.querySelectorAll('.tf-tarea').forEach((f) => f.classList.toggle('tf-tarea--seleccionada', f.dataset.uid === uid));
  renderDetalle(p, uid, ctx);
}
function cerrarDetalle(volver = true) {
  const uid = detalleUid;
  detalleUid = null;
  cerrarEditorDetalle();
  const p = $('#detalle');
  if (!p) return;
  p.classList.remove('abierto'); p.innerHTML = '';
  $('#marco').classList.remove('con-detalle');
  document.querySelectorAll('.tf-tarea--seleccionada').forEach((f) => f.classList.remove('tf-tarea--seleccionada'));
  if (volver && location.pathname.startsWith('/tarea/')) {
    history.pushState(null, '', vistaActual || '/hoy');
    marcarNav((vistaActual || '/hoy').split('?')[0]);
    ponerTitulo(RUTAS.find(([r]) => r.test((vistaActual || '/hoy').split('?')[0]))?.[1].titulo || 'TaskFlow');
  }
  document.querySelector(`[data-uid="${uid}"] a`)?.focus();
}

// ---------- Crear desde cualquier lugar ----------
function listaDeLaVista() {
  const m = location.pathname.match(/^\/lista\/([0-9a-f-]{36})$/);
  return m ? m[1] : null;
}
function nuevaTareaAqui() { nuevaTarea(ctx, { listaUid: listaDeLaVista() }); }
async function nuevaNotaAqui() {
  const m = location.pathname.match(/^\/pila\/([0-9a-f-]{36})$/);
  let pilaUid = m ? m[1] : null;
  if (!pilaUid) {
    const { pilas } = await api.get('/api/pilas');
    const ed = pilas.filter((p) => p.rol !== 'lector');
    if (!ed.length) { aviso('Primero crea una pila: es la carpeta donde viven tus notas.'); return; }
    if (ed.length === 1) pilaUid = ed[0].uid;
    else {
      const d = dialogo(`<form method="dialog" class="cuerpo-dialogo"><h2>Nueva nota</h2><div class="tf-campo"><label for="nn-p">¿En qué pila?</label><select id="nn-p" class="control">${ed.map((p) => `<option value="${e(p.uid)}">${e(p.nombre)}</option>`).join('')}</select></div>
        <div class="acciones"><button class="tf-boton" value="cancelar">Cancelar</button><button class="tf-boton tf-boton--principal" value="ok">Crear nota</button></div></form>`);
      pilaUid = await new Promise((res) => d.addEventListener('close', () => res(d.returnValue === 'ok' ? d.querySelector('#nn-p').value : null)));
      if (!pilaUid) return;
    }
  }
  const uid = uuid();
  try { await api.enviar('/api/notas', { uid, pila_uid: pilaUid, titulo: '' }, 'Crear nota'); ir('/nota/' + uid); } catch (err) { aviso(err.message); }
}

// ---------- Atajos de teclado ----------
let prefijoG = 0;
document.addEventListener('keydown', (ev) => {
  if (!estado.usuario) return;
  const t = ev.target;
  const escribiendo = /INPUT|TEXTAREA|SELECT/.test(t.tagName) || t.isContentEditable;
  if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'z' && !escribiendo) { if (deshacerUltimo()) ev.preventDefault(); return; }
  if (ev.key === 'Escape' && detalleUid && !document.querySelector('dialog[open]') && !document.querySelector('.menu')) { cerrarDetalle(); return; }
  if (escribiendo || ev.ctrlKey || ev.metaKey || ev.altKey || document.querySelector('dialog[open]')) return;
  if (prefijoG && Date.now() - prefijoG < 1200) {
    const destino = { h: '/hoy', s: '/proximos', p: '/pendientes', c: '/calendario', i: '/pilas', a: '/asignadas' }[ev.key.toLowerCase()];
    prefijoG = 0;
    if (destino) { ev.preventDefault(); ir(destino); }
    return;
  }
  if (ev.key === 'g' || ev.key === 'G') { prefijoG = Date.now(); return; }
  if (ev.key === 'n') { ev.preventDefault(); nuevaTareaAqui(); }
  else if (ev.key === 'N') { ev.preventDefault(); nuevaNotaAqui(); }
  else if (ev.key === '/') { ev.preventDefault(); const q = $('#nav-q'); if (q && q.offsetParent) q.focus(); else ir('/buscar'); }
  else if (ev.key === '?') { ev.preventDefault(); verAtajos(); }
});

// Enlaces internos sin recargar la página.
document.addEventListener('click', (ev) => {
  const a = ev.target.closest('a[data-ruta]');
  if (!a || ev.defaultPrevented || ev.ctrlKey || ev.metaKey || ev.shiftKey || a.target === '_blank') return;
  ev.preventDefault();
  if (a.closest('dialog')) a.closest('dialog').close();
  ir(a.getAttribute('href'));
});
window.addEventListener('popstate', () => enrutar());

// ---------- Notificaciones (insignia) ----------
function pintarInsignia(n) {
  const i = $('#insignia');
  if (i) { i.hidden = !n; i.textContent = n > 99 ? '99+' : String(n); $('#campana')?.setAttribute('aria-label', n ? `Notificaciones, ${n} sin leer` : 'Notificaciones'); }
  const c = document.querySelector('[data-contador="/notificaciones"]'); if (c) c.textContent = n || '';
}
alEvento((ev) => {
  if (ev.tipo === 'notificacion') {
    const i = $('#insignia'); pintarInsignia((Number(i?.textContent) || 0) + 1);
    aviso(ev.titulo);
  }
});

// ---------- Instalación de la PWA ----------
window.addEventListener('beforeinstallprompt', (ev) => { ev.preventDefault(); instalarEvento = ev; });
function pintarInstalar(cont) {
  const instalada = matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (instalada) { cont.innerHTML = `<div class="instalar"><img src="/iconos/icono-192.png" alt=""><p style="margin:0">TaskFlow ya está instalada en este dispositivo.</p></div>`; return; }
  cont.innerHTML = `<div class="instalar"><img src="/iconos/icono-192.png" alt=""><div style="flex:1;min-width:200px"><b>Instala TaskFlow</b><p class="meta" style="margin:0">Ábrela desde la pantalla de inicio, a pantalla completa y sin conexión.${ios ? ' En iPhone: toca Compartir y luego «Agregar a inicio».' : ''}</p></div>
    ${instalarEvento ? '<button class="tf-boton tf-boton--principal" type="button" id="instalar">Instalar app</button>' : ''}</div>`;
  cont.querySelector('#instalar')?.addEventListener('click', async () => { instalarEvento.prompt(); const r = await instalarEvento.userChoice; instalarEvento = null; if (r.outcome === 'accepted') aviso('TaskFlow instalada'); pintarInstalar(cont); });
}

// ---------- Sesión ----------
async function entrar(d) {
  estado.csrf = d.csrf;
  estado.usuario = d.usuario;
  estado.vapid = d.vapid || '';
  estado.fichaEventos = d.ficha_eventos || '';
  estado.correoDisponible = !!d.correo_disponible;
  aplicarTema(d.usuario.tema, d.usuario.paleta);
  pintarMarco();
  raiz.removeAttribute('aria-busy');
  if (['/entrar', '/recuperar', '/'].includes(location.pathname)) history.replaceState(null, '', '/hoy');
  await enrutar();
  pintarInsignia(d.sin_leer || 0);
  conectarEventos();
  contarPendientes().then(() => { if (estado.pendientes) sincronizar(); });
  if (d.usuario.debe_cambiar_clave) pedirCambioClave();
  const q = new URLSearchParams(location.search);
  if (q.get('nueva') === 'tarea') nuevaTareaAqui();
  try { localStorage.setItem('tf-usuario', JSON.stringify(d.usuario)); } catch { /* */ }
}

async function salir() {
  const pend = await operacionesPendientes();
  if (pend.length && !await confirmar({ titulo: '¿Cerrar sesión con cambios sin enviar?', texto: `Hay ${plural(pend.length, 'cambio', 'cambios')} guardados solo en este dispositivo. Se enviarán cuando vuelvas a entrar aquí con conexión.`, boton: 'Cerrar sesión' })) return;
  try { await api.post('/api/acceso/salir'); } catch { /* */ }
  desconectarEventos();
  navigator.serviceWorker?.controller?.postMessage('limpiar-api');
  try { localStorage.removeItem('tf-usuario'); } catch { /* */ }
  estado.usuario = null;
  history.replaceState(null, '', '/entrar');
  iniciar();
}
window.addEventListener('tf:sesion-terminada', () => {
  if (!estado.usuario) return;
  estado.usuario = null; desconectarEventos();
  history.replaceState(null, '', '/entrar');
  pantallaEntrar(raiz, entrar, { mensaje: 'Tu sesión terminó. Vuelve a iniciar sesión.' });
});

async function iniciar() {
  try {
    const d = await api.get('/api/acceso/yo');
    estado.csrf = d.csrf; estado.correoDisponible = !!d.correo_disponible;
    if (d.usuario) { await entrar(d); return; }
  } catch (err) {
    // Sin conexión y con sesión previa en este dispositivo: se abre con la copia guardada.
    let previo = null; try { previo = JSON.parse(localStorage.getItem('tf-usuario') || 'null'); } catch { /* */ }
    if (previo && err.sinConexion) { await entrar({ usuario: previo, csrf: '', sin_leer: 0 }); return; }
  }
  raiz.removeAttribute('aria-busy');
  if (location.pathname === '/recuperar') pantallaRecuperar(raiz, entrar);
  else { if (location.pathname !== '/entrar') history.replaceState(null, '', '/entrar'); pantallaEntrar(raiz, entrar); }
}

// ---------- Service worker ----------
if ('serviceWorker' in navigator) {
  // Solo se avisa de una versión nueva si ya había una versión anterior controlando la página.
  const habiaVersion = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.register('/sw.js').then((reg) => {
    reg.addEventListener('updatefound', () => {
      const nuevo = reg.installing;
      nuevo?.addEventListener('statechange', () => { if (nuevo.state === 'activated' && habiaVersion) aviso('Hay una versión nueva de TaskFlow. Recarga la página para usarla.', { duracion: 10000 }); });
    });
  }).catch(() => {});
}

iniciar();
